import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Megaphone, ExternalLink } from "lucide-react";

const fmtDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

export default function WhatsNew() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["releases"], queryFn: async () => (await api.get("/releases")).data });
  const releases = data?.releases || [];
  const unseen = data?.unseen || [];

  useEffect(() => {
    if (unseen.length) {
      Promise.all(unseen.map((rid) => api.post(`/releases/${rid}/seen`)))
        .then(() => qc.invalidateQueries({ queryKey: ["releases"] }));
    }
  }, [unseen.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="whats-new-page">
      <PageHeader
        title="What's New"
        subtitle="Release notes and feature guides — every update to CCFI Compass, newest first."
        breadcrumb={["System", "What's New"]}
      />
      <div className="px-6 lg:px-10 py-6 space-y-4 max-w-3xl">
        {releases.map((r) => (
          <Card key={r.id} className="rounded-sm border-slate-200 industrial-shadow p-5" data-testid={`release-card-${r.id}`}>
            <div className="flex items-start gap-4">
              <span className="shrink-0 w-10 h-10 rounded-sm bg-slate-900 text-cyan-400 flex items-center justify-center">
                <Megaphone size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="stamp-label text-slate-400">{fmtDate(r.date)}</div>
                <h3 className="font-display text-lg font-bold mt-0.5">{r.title}</h3>
                <p className="text-sm text-slate-500 mt-1.5">{r.summary}</p>
                <Button asChild variant="outline" size="sm" className="rounded-sm mt-3">
                  <a href={r.url} target="_blank" rel="noreferrer" data-testid={`read-release-${r.id}`}>
                    <ExternalLink size={13} className="mr-1.5" />Read the full notes
                  </a>
                </Button>
              </div>
            </div>
          </Card>
        ))}
        {releases.length === 0 && (
          <Card className="rounded-sm border-slate-200 p-10 text-center text-sm text-slate-400" data-testid="releases-empty">
            No release notes yet.
          </Card>
        )}
      </div>
    </div>
  );
}
